import React from 'react'

import { cn } from '@/lib/utils'

import { Badge, type BadgeTone } from '@/components/ui/badge'

export type StatusType = 'product' | 'user' | 'order' | 'payment' | 'common'

export interface StatusConfig {
  label: string
  tone: BadgeTone
  icon?: React.ReactNode
}

/** Status → label and tone, following Shopify's badge tones (neutral for done/inactive, yellow for pending). */
export const statusConfigs: Record<StatusType, Record<string, StatusConfig>> = {
  product: {
    active: {
      label: 'Active',
      tone: 'success',
    },
    inactive: {
      label: 'Inactive',
      tone: 'soft',
    },
  },
  user: {
    active: {
      label: 'Active',
      tone: 'success',
    },
    inactive: {
      label: 'Inactive',
      tone: 'soft',
    },
    // blocked: {
    //     label: 'Blocked',
    //     className: 'bg-destructive/10 text-destructive border-destructive/20',
    // },
  },
  order: {
    // order statuses are correct
    active: {
      label: 'Active',
      tone: 'success',
    },
    pending: {
      label: 'Pending',
      tone: 'orange',
    },
    ready_to_ship: {
      label: 'Ready to Ship',
      tone: 'info',
    },
    dispatched: {
      label: 'Dispatched',
      tone: 'info',
    },
    on_the_way: {
      label: 'On the Way',
      tone: 'info',
    },
    delivered: {
      label: 'Delivered',
      tone: 'success',
    },
    cancelled: {
      label: 'Cancelled',
      tone: 'error',
    },
    returned: {
      label: 'Returned',
      tone: 'soft',
    },
    partially_returned: {
      label: 'Partially Returned',
      tone: 'error',
    },
  },

  payment: {
    pending: {
      label: 'Pending',
      tone: 'orange',
    },
    success: {
      label: 'Paid',
      tone: 'soft',
    },
    failed: {
      label: 'Failed',
      tone: 'error',
    },
    partial: {
      label: 'Partial',
      tone: 'warning',
    },
    refunded: {
      label: 'Refunded',
      tone: 'soft',
    },
    canceled: {
      label: 'Canceled',
      tone: 'error',
    },
    rejected: {
      label: 'Rejected',
      tone: 'error',
    },
    attempted: {
      label: 'Attempted',
      tone: 'error',
    },
  },
  common: {
    active: {
      label: 'Active',
      tone: 'success',
    },
    inactive: {
      label: 'Inactive',
      tone: 'soft',
    },
    success: {
      label: 'Success',
      tone: 'success',
    },
    warning: {
      label: 'Warning',
      tone: 'warning',
    },
    error: {
      label: 'Error',
      tone: 'error',
    },
    info: {
      label: 'Info',
      tone: 'info',
    },
  },
}

export interface StatusBadgeProps {
  type?: StatusType
  status: string
  className?: string
  showIcon?: boolean
}

export const StatusBadge = ({ type = 'common', status, className, showIcon = false }: StatusBadgeProps) => {
  const config = statusConfigs[type]?.[status] ||
    statusConfigs.common?.[status] || {
    label: status,
    tone: 'soft',
  }

  return (
    <Badge tone={config.tone} className={cn('capitalize', className)}>
      {showIcon && config?.icon && <span className="mr-1">{config.icon}</span>}
      {config.label}
    </Badge>
  )
}
